import React, { useState, useEffect } from 'react';
import { Container, Header, Box, Grid } from '@cloudscape-design/components';

interface Feature {
  title: string;
  description: string | React.ReactElement;
  icon: string;
}

export const getNewFeatures = (): Feature[] => {
  return [
    {
      title: 'Overdue Task Completion',
      description:
        'Tasks can now be completed after the due date. Late completions are automatically flagged, and due dates can be extended up to 10 days if the task has not yet expired.',
      icon: '⏱️',
    },
    {
      title: 'Configurable Mandatory Inputs for Failed Questions',
      description:
        'Admins can configure mandatory comments and/or evidence for failed audit questions at the question level. Settings apply to all future audits using that question version.',
      icon: '⚙️',
    },
    {
      title: 'Validation for Mandatory Inputs',
      description:
        'Auditors are notified when required comments or evidence are missing and cannot close the audit until all mandatory inputs are completed.',
      icon: '🚨',
    },
    {
      title: 'Draft Tasks During Audits',
      description:
        'Auditors can create draft follow-up tasks while answering audit questions, ensuring tasks are captured in context and finalized when the audit closes.',
      icon: '📝',
    },
    {
      title: 'Edit Default Tasks in Audits',
      description:
        'Default task descriptions can be edited during the audit to include site-specific context.',
      icon: '✏️',
    },
    {
      title: 'Audit Observations',
      description:
        'Users can continue creating observations not tied to failed questions to capture broader findings.',
      icon: '🔍',
    },
    {
      title: 'Create & Edit Tasks from Audit Summary',
      description:
        'Auditors can create or edit tasks directly from the Audit Summary page with full visibility into related audit questions.',
      icon: '📋',
    },
  ];
};

const WhatsNewSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const features: Feature[] = getNewFeatures();
  const FEATURES_PER_VIEW = 3;
  const totalGroups = Math.ceil(features.length / FEATURES_PER_VIEW);

  // Auto-scroll every 9 seconds with accessibility consideration
  useEffect(() => {
    if (totalGroups <= 1) return;

    // Respect user's motion preferences
    const prefersReducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalGroups);
    }, 9000);

    return () => clearInterval(interval);
  }, [totalGroups]);

  // Don't render if no features
  if (features.length === 0) {
    return null;
  }

  // Get current group of features to display
  const getCurrentFeatures = () => {
    const startIndex = currentIndex * FEATURES_PER_VIEW;
    return features.slice(startIndex, startIndex + FEATURES_PER_VIEW);
  };

  return (
    <Container
      data-testid="whats-new-container"
      id="whats-new-section"
      header={
        <Header variant="h2" data-testid="whats-new-header">
          ✨ What's New
        </Header>
      }
    >
      <Box padding="s">
        <Grid
          gridDefinition={[
            { colspan: { default: 12, xs: 4 } },
            { colspan: { default: 12, xs: 4 } },
            { colspan: { default: 12, xs: 4 } },
          ]}
        >
          {getCurrentFeatures().map((feature, index) => (
            <Box key={`${currentIndex}-${index}`} padding="s">
              <Box variant="awsui-key-label" padding="xs">
                <Box>
                  <div className="whats-new-title-row">
                    <Box margin={{ right: 'xs' }} fontSize="heading-l">
                      {feature.icon}
                    </Box>
                    <Box variant="strong" fontSize="body-m">
                      {feature.title}
                    </Box>
                  </div>
                  <Box variant="p" color="text-body-secondary">
                    {feature.description}
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Grid>

        {totalGroups > 1 && (
          <Box textAlign="center" margin={{ top: 's' }} data-testid="whats-new-dots">
            {Array.from({ length: totalGroups }).map((_, index) => (
              <span
                key={index}
                data-testid={`dot-${index}`}
                onClick={() => setCurrentIndex(index)}
                style={{
                  display: 'inline-block',
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: index === currentIndex ? '#0972d3' : '#d5dbdb',
                  margin: '0 4px',
                  cursor: 'pointer',
                }}
                aria-label={`Go to feature group ${index + 1}`}
              />
            ))}
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default WhatsNewSection;
